import React, { useState } from 'react'
import { EditorState } from 'draft-js'
import UploadMedia from 'components/editor/components/UploadMedia'
import IframeInsert from 'components/editor/components/IframeInsert'
import DashboardInsert, { DashboardEmbedData } from 'components/editor/components/DashboardInsert'
import SlicerInsert from 'components/editor/components/SlicerInsert'
import { HighlighterColors, HighlighterIcon } from 'components/editor/components/Highlighter'
import { AddAnchorControl, RemoveAnchorControl } from 'components/editor/components/Anchor'
import { IFileParentResource, IContextMenuState } from 'types'
import { Bold, Italic, Underline, Bullet, NumberedBullet } from 'components/icons'
import { useLocation } from 'react-router-dom'
import Menu from 'components/menu'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import { selectionBlockExists } from 'components/editor/helpers/selection'

interface StyleButtonProps {
  active: boolean
  icon: any
  onToggle: (controlType: 'INLINE' | 'BLOCK', style: any) => void
  style: any
  controlType: 'INLINE' | 'BLOCK'
  title: string
}

const StyleButton: React.FC<StyleButtonProps> = ({
  active,
  icon,
  onToggle,
  style,
  controlType,
  title
}: StyleButtonProps) => {
  const ButtonIcon = icon
  let className =
    'flex items-center hover:bg-surface-sunken transition-all rounded RichEditor-styleButton select-none cursor-pointer'
  if (active) className += ' RichEditor-activeButton'

  return (
    <span
      title={title}
      className={`${className} p-[7px]`}
      onMouseDown={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onToggle(controlType, style)
      }}
    >
      <ButtonIcon className="h-[25px] w-[25px]" />
    </span>
  )
}

interface StyleControlProps {
  editorState: any
  onToggle: (controlType: 'INLINE' | 'BLOCK', style: any) => void
}

export const InlineStyleControls: React.FC<StyleControlProps> = ({ editorState, onToggle }: StyleControlProps) => {
  const currentStyle = selectionBlockExists(editorState) ? editorState.getCurrentInlineStyle() : null

  const INLINE_STYLES = [
    { label: 'Bold', style: 'BOLD', icon: Bold, title: 'Bold' },
    { label: 'Italic', style: 'ITALIC', icon: Italic, title: 'Italic' },
    { label: 'Underline', style: 'UNDERLINE', icon: Underline, title: 'Underline' }
  ]

  return (
    <div className="RichEditor-controls">
      {INLINE_STYLES.map((type) => (
        <StyleButton
          key={type.label}
          active={currentStyle ? currentStyle.has(type.style) : false}
          icon={type.icon}
          onToggle={onToggle}
          style={type.style}
          controlType="INLINE"
          title={type.title}
        />
      ))}
    </div>
  )
}

export const BlockStyleControls: React.FC<StyleControlProps> = ({ editorState, onToggle }: StyleControlProps) => {
  const selection = selectionBlockExists(editorState) ? editorState.getSelection() : null
  const blockType = selection ? editorState.getCurrentContent().getBlockForKey(selection.getStartKey()).getType() : null

  const BLOCK_TYPES = [
    { label: 'UL', style: 'unordered-list-item', icon: Bullet, title: 'Add Bulleted List' },
    { label: 'OL', style: 'ordered-list-item', icon: NumberedBullet, title: 'Add Numbered List' }
  ]

  return (
    <div className="RichEditor-controls">
      {BLOCK_TYPES.map((type) => (
        <StyleButton
          key={type.label}
          active={blockType ? type.style === blockType : false}
          icon={type.icon}
          onToggle={onToggle}
          style={type.style}
          controlType="BLOCK"
          title={type.title}
        />
      ))}
    </div>
  )
}

interface ControlPanelProps {
  editorState: any
  // The editor's live state, for inserts that finish after awaiting the API.
  getEditorState?: () => EditorState
  onToggle: (controlType: 'INLINE' | 'BLOCK', style: any) => void
  onChange: (newContent: any) => void
  addIframeModifier: (editorState: EditorState, url: string, height: number) => EditorState
  processId?: string
  sectionId?: string
  addDashboardModifier: (editorState: EditorState, data: DashboardEmbedData, entityKey?: string) => EditorState
  addSlicerModifier: (
    editorState: EditorState,
    tableId: string,
    viewId: string,
    columnId: string,
    entityKey?: string
  ) => EditorState
  resources: IFileParentResource[]
  insertUploadProgress: (
    editorState: EditorState,
    mediaType: 'image' | 'video' | 'file',
    filename: string
  ) => { editorState: EditorState; entityKey: string } | null
  updateUploadProgress: (entityKey: string, progress: number) => void
  replaceUploadProgressWithMedia: (
    progressEntityKey: string,
    mediaType: 'image' | 'video' | 'file',
    mediaData: { url: string; filename?: string; extra?: any } | null
  ) => EditorState
}

const ControlPanel: React.FC<ControlPanelProps> = ({
  editorState,
  getEditorState,
  onToggle,
  onChange,
  addIframeModifier,
  processId,
  sectionId,
  addDashboardModifier,
  addSlicerModifier,
  resources,
  insertUploadProgress,
  updateUploadProgress,
  replaceUploadProgressWithMedia
}: ControlPanelProps) => {
  const [highlightMenu, setHighlightMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)

  const location = useLocation()
  const inTableDeactivate = location.pathname.includes('table')

  return (
    <div className="w-full sticky top-0 z-[2]">
      <div
        role="toolbar"
        aria-label="Editor formatting controls"
        className="w-full flex items-center rounded border border-divider-strong bg-surface overflow-auto py-0 px-2.5"
        tabIndex={0}
      >
        <InlineStyleControls editorState={editorState} onToggle={onToggle} />
        <BlockStyleControls editorState={editorState} onToggle={onToggle} />
        <AddAnchorControl editorState={editorState} onChange={onChange} />
        <RemoveAnchorControl editorState={editorState} onChange={onChange} />
        <div>
          <HighlighterIcon
            onClick={(event: React.MouseEvent) => {
              const rect = event.currentTarget.getBoundingClientRect()
              setHighlightMenu({
                ...INITIAL_CONTEXT_MENU_STATE,
                open: true,
                top: `${rect.bottom + 5}px`,
                left: `${rect.left}px`
              })
            }}
          />
          {highlightMenu.open && (
            <Menu id="highlighter-menu" menuState={highlightMenu} setMenuState={setHighlightMenu} width={220}>
              <HighlighterColors editorState={editorState} onChange={onChange} />
            </Menu>
          )}
        </div>
        <div className="flex items-center border-l-[1px] border-divider pl-2.5">
          <UploadMedia
            editorState={editorState}
            onChange={onChange}
            mediaType="image"
            key={'image'}
            resources={resources}
            insertUploadProgress={insertUploadProgress}
            updateUploadProgress={updateUploadProgress}
            replaceUploadProgressWithMedia={replaceUploadProgressWithMedia}
          />

          <UploadMedia
            mediaType="video"
            editorState={editorState}
            onChange={onChange}
            key={'video'}
            resources={resources}
            insertUploadProgress={insertUploadProgress}
            updateUploadProgress={updateUploadProgress}
            replaceUploadProgressWithMedia={replaceUploadProgressWithMedia}
          />
          <UploadMedia
            mediaType="file"
            editorState={editorState}
            onChange={onChange}
            key={'file'}
            resources={resources}
            insertUploadProgress={insertUploadProgress}
            updateUploadProgress={updateUploadProgress}
            replaceUploadProgressWithMedia={replaceUploadProgressWithMedia}
          />
        </div>
        {!inTableDeactivate && (
          <div className="flex items-center ml-2.5 pl-2.5 gap-2.5 border-l border-l-divider">
            <DashboardInsert
              editorState={editorState}
              getEditorState={getEditorState}
              onChange={onChange}
              modifier={addDashboardModifier}
              processId={processId}
              sectionId={sectionId}
            />
            <IframeInsert editorState={editorState} onChange={onChange} modifier={addIframeModifier} />
            <SlicerInsert editorState={editorState} onChange={onChange} modifier={addSlicerModifier} />
          </div>
        )}
      </div>
    </div>
  )
}

export default ControlPanel
